import React, { useEffect, useState } from 'react';
import DialogComponent from '../dialog/dialog.component';
import VerifyBadge from '../verify-badge/verify-badge.component';
import { useSelector } from 'react-redux';
import { RootState } from '../../configuration';
import { donateUser, fetchBalance } from '../../api';
import toast from 'react-hot-toast';
import { InputNumber } from 'antd';
import DialogPayment from '../dialog-payment';
import { InputWarning } from '../inputwarning/input-warning.component';
import PetPet from '../petpet/petpet.component';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import VoiceRecorder, { VoiceRecorderValue } from '../donate-voice/voice-recorder';
import { submitWithVoice } from '../donate-voice/submit-with-voice';
import { uploadVoiceMessage } from '../../api/payment/donate-voice.api';
import SectionTabs from '../section-tabs/section-tabs';
import CreatorMusicRequest from '../../pages/profile-user/components/creator-music-request';
import CreatorLiveEffect from '../../pages/profile-user/components/creator-live-effect';
import CreatorDonatePoll from '../../pages/profile-user/components/creator-donate-poll';
import CreatorSpinWheel from '../../pages/profile-user/components/creator-spin-wheel';
import AiQuestionAsk from '../ai-question/ai-question-ask';
import { loadDonateFeatureStatus } from '../donate-kinds/donate-kinds';

// Các kiểu donate trong hộp thoại, theo thứ tự tab. Tab "Donate" luôn có; các tab sau chỉ hiện
// khi streamer đã bật tính năng đó (mỗi khối tự báo qua onAvailableChange, giống tab Donate trên
// trang cá nhân). Trước đây hộp thoại chỉ có donate thường, người xem không biết còn các kiểu khác.
const DIALOG_TABS: Array<{ key: string; label: string }> = [
  { key: 'donate', label: 'Donate' },
  { key: 'nhac', label: 'Gửi nhạc' },
  { key: 'hoi-ai', label: 'Hỏi AI' },
  { key: 'hieu-ung', label: 'Hiệu ứng' },
  { key: 'binh-chon', label: 'Bình chọn' },
  { key: 'vong-quay', label: 'Vòng quay' },
];

const DonateUser = ({ user }: any) => {
  const { isOn } = useFeatureFlags();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [petpet, setPetpet] = useState<{ src?: string; label: string } | null>(null);
  const profileUser = useSelector(
    (state: RootState) => state.features.auth.profile
  );
  const [donationAmount, setDonationAmount] = useState<any>();
  const [submitting, setSubmitting] = useState(false); // QA S-14
  const [content, setContent] = useState('');
  // Ẩn danh: chỉ che ở các bề mặt CÔNG KHAI (lịch sử donate, overlay livestream).
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [balance, setBalance] = useState(0);
  const [voiceClip, setVoiceClip] = useState<VoiceRecorderValue | null>(null);
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const [activeTab, setActiveTab] = useState('donate');
  const [featureAvailable, setFeatureAvailable] = useState<Record<string, boolean>>({});
  const handleAvailable = (key: string) => (value: boolean) =>
    setFeatureAvailable((prev) => (prev[key] === value ? prev : { ...prev, [key]: value }));
  // Các kiểu streamer đang nhận, đọc sẵn khi vào trang để hiện hàng nhãn "Cách ủng hộ khác" cạnh nút
  // Donate (người xem biết ngay streamer nhận gì mà không phải mở hộp). Tab trong hộp hiện khi danh sách
  // này có HOẶC khối thật báo đang bật: khối thật báo "chưa bật" ngay lúc vừa dựng (dữ liệu chưa về),
  // nếu chỉ tin khối thì bấm nhãn sẽ rơi về tab Donate rồi mới nhảy sang.
  const [offered, setOffered] = useState<string[]>([]);
  const donateOn = isOn('feature.donate') && user?.canReceiveDonation !== false;
  useEffect(() => {
    setOffered([]);
    if (!user?.userId || !donateOn) return undefined;
    let alive = true;
    const extraKeys = DIALOG_TABS.filter((t) => t.key !== 'donate').map((t) => t.key);
    loadDonateFeatureStatus(user.userId, extraKeys)
      .then((r) => alive && setOffered(r.filter((x) => x.state === 'on').map((x) => x.key)))
      .catch(() => alive && setOffered([]));
    return () => {
      alive = false;
    };
  }, [user?.userId, donateOn]);
  const dialogTabs = DIALOG_TABS.filter(
    (t) => t.key === 'donate' || featureAvailable[t.key] || offered.includes(t.key)
  );
  const offeredTabs = DIALOG_TABS.filter((t) => t.key !== 'donate' && offered.includes(t.key));
  // Tab đang chọn biến mất (streamer vừa tắt tính năng) thì về tab Donate.
  const currentTab = dialogTabs.some((t) => t.key === activeTab) ? activeTab : 'donate';

  const handleContentChange = (e) => {
    setContent(e.target.value);
  };
  // Function to toggle dialog visibility
  // Đóng hộp thoại thì các khối tính năng bị gỡ, lần mở sau luôn bắt đầu từ tab Donate.
  const closeDialog = () => {
    setIsDialogOpen(false);
    setActiveTab('donate');
  };
  const openAt = (key: string) => {
    setActiveTab(key);
    setIsDialogOpen(true);
  };
  const toggleDialog = () => {
    if (isDialogOpen) closeDialog();
    else setIsDialogOpen(true);
  };
  // State for the money input
  // Function to handle changes to the input field

  const handleSubmit = () => {
    if (!donationAmount) {
      toast.error('Vui lòng nhập số tiền bạn muốn donate.');
      return;
    }
    if (submitting) return; // QA S-14: chặn donate 2 lần
    setSubmitting(true);

    const donate = (voiceClipId?: string) =>
      donateUser({
        toUserId: user.userId,
        message: content,
        amount: donationAmount,
        isAnonymous,
        ...(voiceClipId ? { voiceClipId } : {}),
      })
        .then((res) => {
          const total = `${donationAmount}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
          toast.success(
            `Bạn đã donate ${total} ca thành công cho ${user?.fullname}`,
            {
              duration: 4000,
              position: 'top-center',
            }
          );
          // Hiệu ứng Pet Pet: vỗ đầu avatar người nhận để "cảm ơn".
          setPetpet({
            src: user?.avatar,
            label: `Đã vỗ đầu ${user?.fullname || ''} · +${total} ca 💛`,
          });
          setDonationAmount(null);
          setIsAnonymous(false);
          setVoiceClip(null);
          toggleDialog();
        })
        .catch((err) => {
          toast.error(err?.response?.data?.message || 'Donate thất bại, vui lòng thử lại.');
        })
        .finally(() => setSubmitting(false));

    // Có ghi âm ⇒ tải lên media-service trước để lấy voiceClipId, lỗi thì dừng, không donate.
    submitWithVoice(voiceClip, donate, uploadVoiceMessage).catch(() => setSubmitting(false));
  };

  function formatVND(value) {
    const amount = Number(value);
    if (isNaN(amount)) return '0 đồng'; // Return "0 VND" if the value is not a number

    // Format the number with commas and add " VND" suffix
    return `${amount
      .toFixed(0)
      .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')} đồng`;
  }
  useEffect(() => {
    // Rà soát 28/10: chỉ Ca NẠP mới donate được, nên hiện số Ca nạp thay vì tổng số dư (gồm Ca thu nhập).
    fetchBalance().then((res) => {
      setBalance(res.topUpBalance ?? res.balance);
    });
  }, []);

  if (!isOn('feature.donate')) return null; // admin tắt donate

  return (
    <>
      <div className="flex w-[336px] flex-col gap-2 max-md:w-full max-md:flex-1">
      <button
        className="w-full rounded-xl gap-[10px] bg-[#F6F8FF] backdrop-blur-sm justify-center  border border-[#DEE8FF] flex items-center text-white py-2 px-3.5 max-md:flex-1"
        onClick={toggleDialog}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M5.84961 10.8002L8.39961 15.7502L13.1496 13.8002L15.3496 7.4502L7.94961 7.8502L5.84961 10.8002Z"
            fill="#E69329"
          />
          <path
            d="M7.49961 21.8996C9.65352 21.8996 11.3996 20.1535 11.3996 17.9996C11.3996 15.8457 9.65352 14.0996 7.49961 14.0996C5.3457 14.0996 3.59961 15.8457 3.59961 17.9996C3.59961 20.1535 5.3457 21.8996 7.49961 21.8996Z"
            fill="#546E7A"
          />
          <path
            d="M7.5 13.5C5 13.5 3 15.5 3 18C3 20.5 5 22.5 7.5 22.5C10 22.5 12 20.5 12 18C12 15.5 10 13.5 7.5 13.5ZM7.5 21.5C5.55 21.5 4 19.95 4 18C4 16.05 5.55 14.5 7.5 14.5C9.45 14.5 11 16.05 11 18C11 19.95 9.45 21.5 7.5 21.5Z"
            fill="#90A4AE"
          />
          <path d="M7 16.5H8V20.5H7V16.5Z" fill="#90A4AE" />
          <path
            d="M6.44854 17.9998C6.94854 18.9498 8.04854 19.3498 8.99854 18.8498L17.2485 14.5998C17.7485 14.3498 18.0985 13.9998 18.3485 13.6498C19.1985 12.0498 21.1485 8.2998 22.4485 5.0498L13.3485 9.3998L10.9485 12.9998L7.54854 14.7998C6.24854 15.4498 5.84854 16.8998 6.44854 17.9998Z"
            fill="#FFB74D"
          />
          <path
            d="M15.0994 1.5L6.84939 4.65C6.49939 4.75 6.09939 5.15 5.74939 5.5L2.94939 9.25C2.44939 10 2.34939 10.95 2.69939 11.8C2.89939 12.3 3.54939 13.5 4.24939 14.85C5.04939 14 6.19939 13.5 7.49939 13.5C7.69939 13.5 7.94939 13.5 8.14939 13.55L7.09939 11.45L9.39939 9.4H13.3994C13.3994 9.4 21.1494 8.3 22.4994 5.05L15.0994 1.5Z"
            fill="#FFB74D"
          />
          <path
            d="M9.10073 17.9998C8.45073 18.2998 7.70073 17.9998 7.45073 17.3498C7.15073 16.6998 7.45073 15.9498 8.10073 15.6998C8.70073 15.3998 9.70073 17.6998 9.10073 17.9998Z"
            fill="#FFCDD2"
          />
        </svg>

        <span className="text-base font-medium text-[#0057FF]"> Donate </span>
      </button>
      {/* Người xem thấy ngay streamer nhận những kiểu ủng hộ nào; bấm nhãn là mở hộp donate ở đúng tab đó. */}
      {offeredTabs.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="donate-kinds">
          <span className="text-xs text-[#627184] dark:text-slate-400">Cách ủng hộ khác:</span>
          {offeredTabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => openAt(t.key)}
              className="rounded-full bg-[#EFF7FD] px-2.5 py-1 text-xs font-medium text-[#215C91] hover:bg-[#DCEEFB] dark:bg-slate-700 dark:text-blue-300"
            >
              {t.label}
            </button>
          ))}
        </div>
      )}
      </div>
      <DialogComponent
        onClose={closeDialog}
        visible={isDialogOpen}
        placement="CENTER"
        title="Donate"
        size="full"
        customSize='w-full max-w-[976px]'
      >
        <div className="flex flex-col  ">
          <div className="w-full h-12 flex justify-between px-5 items-center bg-[#ebecf2]">
            <span className="text-sm text-[#111827]  font-semibold">
              Ca nạp dùng để donate
              {currentTab === 'donate' && (
                <span className="font-normal text-[#62677D]"> (tối thiểu 10 Ca mỗi lần)</span>
              )}
            </span>
            <div className="flex items-center gap-1">
              <svg
                width="17"
                height="16"
                viewBox="0 0 17 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.5 12L12.5 4"
                  stroke="#26B35E"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6 4H12.5V10.5"
                  stroke="#26B35E"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>

              <span className="text-sm font-medium uppercase text-[#26B35E]">
                {Number(balance).toLocaleString('vi-VN')} ca
              </span>
            </div>
          </div>
          {dialogTabs.length > 1 && (
            <div className="px-6 pt-4">
              <SectionTabs
                tabs={dialogTabs}
                active={currentTab}
                onChange={setActiveTab}
                sticky={false}
              />
            </div>
          )}
          <div className={`${currentTab === 'donate' ? 'flex' : 'hidden'} gap-3 flex-col p-6`}>
            <p className="text-base font-medium text-[#62677D] leading-5">
              Bạn đang donate tới tài khoản
            </p>
            <div className="flex flex-col gap-4">
              <div className="flex px-3 py-2 gap-2 border-b-[#E5E7EB] dark:border-b-slate-700 border-b">
                <div className="flex-shrink-0">
                  <img
                    className="h-10 w-10 rounded-full"
                    src={
                      user?.avatar ||
                      '/default-avatar.svg'
                    }
                    alt="avatar"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-bold text-[#111827]">
                    {user?.fullname}
                  </span>
                  <p className="text-[#080809] font-medium text-xs truncate ">
                    {user?.description}
                  </p>
                  <p className="text-[#0071A6] font-medium text-xs ">
                    @{user?.username}
                  </p>
                </div>
                <VerifyBadge userType={user?.userType} />
              </div>
              <div className="flex flex-col gap-1">
                <div className="flex items-center">
                  <label
                    htmlFor="donationAmount"
                    className=" font-medium text-sm text-gray-700 dark:text-gray-300"
                  >
                    Số ca
                  </label>
                  &nbsp;
                  <InputWarning />
                </div>
                <InputNumber
                  name="donationAmount"
                  id="donationAmount"
                  value={donationAmount}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                  }
                  onChange={setDonationAmount}
                  className="block w-full rounded-lg pl-3 pr-10 py-2 text-base border-[#C8CCD8] sm:text-sm "
                  placeholder="Nhập số ca"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="content"
                  className=" font-medium text-sm text-gray-700 dark:text-gray-300"
                >
                  Lời nhắn
                </label>
                <textarea
                  rows={3}
                  name="content"
                  id="content"
                  value={content}
                  onChange={handleContentChange}
                  className="mt-1 block w-full border  pl-3 pr-10 py-2 text-base border-[#C8CCD8] focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-lg"
                  placeholder="Nhập nội dung"
                />

                <VoiceRecorder
                  creatorId={user?.userId}
                  amount={Number(donationAmount) || 0}
                  onChange={setVoiceClip}
                  onRecordingChange={setIsRecordingVoice}
                />

                <label className="mt-3 flex items-start gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={isAnonymous}
                    onChange={(e) => setIsAnonymous(e.target.checked)}
                    className="mt-[3px] h-4 w-4 shrink-0 rounded border-[#C8CCD8] accent-blue-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Donate ẩn danh
                    <span className="block text-xs text-gray-500 dark:text-gray-400">
                      Ẩn tên bạn khỏi lịch sử donate công khai và thông báo trên livestream.
                      Người nhận vẫn thấy tên bạn trong thông báo riêng.
                    </span>
                  </span>
                </label>
              </div>
            </div>
          </div>
          {/* Chỉ dựng các khối khi mở hộp thoại để trang cá nhân không gọi thêm 5 API mỗi lần tải.
              Dựng cả khối đang ẩn (hidden, không unmount) để chúng tự báo tính năng có bật không
              và không mất ô nhập dở khi đổi tab. */}
          {isDialogOpen && (
            <div className={currentTab === 'donate' ? 'px-6' : 'px-6 pb-6'}>
              <div hidden={currentTab !== 'nhac'}>
                <CreatorMusicRequest userId={user?.userId} onAvailableChange={handleAvailable('nhac')} />
              </div>
              <div hidden={currentTab !== 'hoi-ai'}>
                <AiQuestionAsk userId={user?.userId} onAvailableChange={handleAvailable('hoi-ai')} />
              </div>
              <div hidden={currentTab !== 'hieu-ung'}>
                <CreatorLiveEffect
                  userId={user?.userId}
                  creatorName={user?.fullname || user?.username || ''}
                  onAvailableChange={handleAvailable('hieu-ung')}
                />
              </div>
              <div hidden={currentTab !== 'binh-chon'}>
                <CreatorDonatePoll userId={user?.userId} onAvailableChange={handleAvailable('binh-chon')} />
              </div>
              <div hidden={currentTab !== 'vong-quay'}>
                <CreatorSpinWheel userId={user?.userId} onAvailableChange={handleAvailable('vong-quay')} />
              </div>
            </div>
          )}
          <div className={`${currentTab === 'donate' ? 'flex' : 'hidden'} p-6 gap-2 items-center justify-end`}>
            <DialogPayment
              onConfirm={(value) => {
                console.log('🚀 ~ PostDonate ~ value:', value);
              }}
              onCancel={() => {
                console.log('🚀 ~ PostDonate ~ value:');
              }}
            />
            <button
              onClick={handleSubmit}
              disabled={submitting || isRecordingVoice}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? 'Đang gửi…' : 'Donate'}
            </button>
          </div>
        </div>
      </DialogComponent>
      {petpet && (
        <PetPet src={petpet.src} label={petpet.label} onDone={() => setPetpet(null)} />
      )}
    </>
  );
};

export default DonateUser;
